import { Box, Fade, Grow, Stack } from "@mui/material";
import { SendingStatus, type UserMessage } from "@sendbird/chat/message";
import {
  CbhIcon,
  type CbhIconProps,
  type IconType,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { useCallback, useState } from "react";

import { useToast } from "../../../lib";
import { DeleteMessageConfirmationBottomSheet } from "./DeleteMessageConfirmationBottomSheet";

const ANIMATION_DELAY_MS = 50;

interface ActionButtonProps {
  delay: number;
  iconColor?: CbhIconProps["color"];
  iconType: IconType;
  isVisible: boolean;
  onClick: () => void;
}

function ActionButton(props: ActionButtonProps) {
  const { delay, iconColor, iconType, isVisible, onClick } = props;

  return (
    <Grow
      in={isVisible}
      style={{ transitionDelay: isVisible ? `${delay}ms` : "0ms" }}
      timeout={150}
    >
      <Box
        component="button"
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 40,
          height: 40,
          borderRadius: "50%",
          border: "none",
          backgroundColor: theme.palette.background.paper,
          boxShadow: theme.shadows[2],
          cursor: "pointer",
          pointerEvents: "auto",
          "&:active": { transform: "scale(0.95)" },
        })}
        onClick={onClick}
      >
        <CbhIcon color={iconColor} size="medium" type={iconType} />
      </Box>
    </Grow>
  );
}

interface MessageActionButtonsProps {
  isMessageByCurrentUser: boolean;
  isReply: boolean;
  isVisible: boolean;
  message: UserMessage;
  onClose: () => void;
  onCopy: () => void;
  onDelete: () => Promise<void>;
  onEdit: () => void;
  onReply: () => void;
  onResend: () => void;
}

export function MessageActionButtons(props: MessageActionButtonsProps) {
  const {
    isMessageByCurrentUser,
    isReply,
    isVisible,
    message,
    onClose,
    onCopy,
    onDelete,
    onEdit,
    onReply,
    onResend,
  } = props;

  const { showSuccessToast, showErrorToast } = useToast();
  const deleteConfirmationModalState = useModalState();
  const [isDeleting, setIsDeleting] = useState(false);

  const isFailed = message.sendingStatus === SendingStatus.FAILED;
  const canDelete = isMessageByCurrentUser && !isFailed;
  const canEdit = isMessageByCurrentUser && !isFailed;
  const canResend = isMessageByCurrentUser && isFailed;
  const canReply = !isFailed && !isReply;

  const handleReply = useCallback(() => {
    onReply();
    onClose();
  }, [onReply, onClose]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.message);
      showSuccessToast("Message copied");
    } catch {
      onCopy();
    }
    onClose();
  }, [message.message, onCopy, onClose, showSuccessToast]);

  const handleDeleteClick = useCallback(() => {
    onClose();
    deleteConfirmationModalState.openModal();
  }, [onClose, deleteConfirmationModalState]);

  const handleDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      deleteConfirmationModalState.closeModal();
    } catch {
      showErrorToast("Failed to delete message");
    } finally {
      setIsDeleting(false);
    }
  }, [onDelete, deleteConfirmationModalState, showErrorToast]);

  const handleDeleteCancel = useCallback(() => {
    deleteConfirmationModalState.closeModal();
  }, [deleteConfirmationModalState]);

  const handleResend = useCallback(() => {
    onResend();
    onClose();
  }, [onResend, onClose]);

  const handleEdit = useCallback(() => {
    onEdit();
    onClose();
  }, [onEdit, onClose]);

  if (!isVisible && !deleteConfirmationModalState.modalIsOpen) {
    return null;
  }

  let buttonIndex = 0;

  return (
    <>
      {/* Overlay */}
      <Fade in={isVisible} timeout={200}>
        <Box
          sx={(theme) => ({
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            zIndex: theme.zIndex.tooltip - 1,
          })}
          onClick={onClose}
        />
      </Fade>

      {/* Action buttons */}
      <Stack
        direction="row"
        gap={1}
        justifyContent={isMessageByCurrentUser ? "flex-end" : "flex-start"}
        sx={(theme) => ({
          position: "absolute",
          bottom: "100%",
          left: 0,
          right: 0,
          paddingBottom: 3,
          zIndex: theme.zIndex.tooltip,
          pointerEvents: "none",
        })}
      >
        {canReply && (
          <ActionButton
            delay={buttonIndex++ * ANIMATION_DELAY_MS}
            iconColor={(theme) => theme.palette.text.primary}
            iconType="chat"
            isVisible={isVisible}
            onClick={handleReply}
          />
        )}

        <ActionButton
          delay={buttonIndex++ * ANIMATION_DELAY_MS}
          iconColor={(theme) => theme.palette.text.primary}
          iconType="copy"
          isVisible={isVisible}
          onClick={handleCopy}
        />

        {canEdit && (
          <ActionButton
            delay={buttonIndex++ * ANIMATION_DELAY_MS}
            iconColor={(theme) => theme.palette.text.primary}
            iconType="pencil"
            isVisible={isVisible}
            onClick={handleEdit}
          />
        )}

        {canResend && (
          <ActionButton
            delay={buttonIndex++ * ANIMATION_DELAY_MS}
            iconColor={(theme) => theme.palette.text.primary}
            iconType="refresh"
            isVisible={isVisible}
            onClick={handleResend}
          />
        )}

        {canDelete && (
          <ActionButton
            delay={buttonIndex++ * ANIMATION_DELAY_MS}
            iconColor={(theme) => theme.palette.error.main}
            iconType="remove"
            isVisible={isVisible}
            onClick={handleDeleteClick}
          />
        )}
      </Stack>

      <DeleteMessageConfirmationBottomSheet
        modalState={deleteConfirmationModalState}
        isLoading={isDeleting}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </>
  );
}
